import { ComputerStrategy } from './ComputerStrategy';
import PathMovement from './PathMovement';
import Path from './Path';
import NodeManager from './NodeManager';
import PathManager from './PathManager';
import Node from './Node';

// de los nodos cercanos, me vas a devolver el nodo con mayor prioridad para atacar
// para priorizar los nodos cercanos debo tener en cuenta los siguientes puntos
// tropas del nodo amigo
// tropas del nodo enemigo
// tipo de propietario del nodo enemigo
// fuerza defensiva del node enemigo
// contador del pathMovement (si hay)
// valor estrategico
// fuerza defensiva del nodo amigo

interface ComputerCounter {
    id: string;
    attackCounter: number;
}

interface PossibleMovement {
    priority: number;
    originNode: Node;
    targetNode: Node;
    percentage: number;
}

interface ComputerStrategyAdvancedConfig {
    // configura saltos en los turnos para que las computadoras no hagan ningun movimiento, por defecto todos los turnos generan movimientos
    slowingMovements: number,
    // configura el umbral apartir del cual se admiten los movimientos de ataque
    attackMovementPriorityThreshold: number,
    // configura el peso a la hora de considerar un ataque segun la defensa del nodo origen
    guardPriorityWeight: number,
    // configura el peso a la hora de considerar un ataque a un objetifo neutral
    neutralTargetWeight: number,
    // configura el peso a la hora de considerar un ataque cuando ganará
    attackWinWeight: number,
    // configura el peso a la hora de considerar un ataque cuando pierde
    attackLoseWeight: number,
    // configura la precision con la que las computadoras seleccionan el mejor movimiento de ataque
    attackComputerAccuracy: number,
    // configura el numero de unidades sobrantes en un ataque que se quedaran en el nodo origen
    attackGuardUnits: number,
    // configura cada cuanto se ejecuta un refuerzo con precision desde el nodo con mas unidades
    slowingPrecisionReinforcements: number
}

export default class ComputerStrategyAdvanced implements ComputerStrategy {

    private mainCounter = 0;
    private reforceCounter = 0;
    private computerCounters: {[player: string]: ComputerCounter} = {};
    private config: ComputerStrategyAdvancedConfig;

    public constructor(config: ComputerStrategyAdvancedConfig) {
        this.config = config;
    }

    public pathMovements(nodeManager: NodeManager, pathManager: PathManager): PathMovement[] {

        const posibleMovements: PossibleMovement[] = [];

        // relentizacion global de movimientos
        const pathMovements: PathMovement[] = [];
        if(this.mainCounter < this.config.slowingMovements) {
            this.mainCounter++;
            return pathMovements;
        }
        this.mainCounter = 0;

        // obtencion de los nodos de una computadora
        const paths: Path[] = pathManager.all();
        const computerNodes = Phaser.Utils.Array.Shuffle(nodeManager.computerNodes());
        const computerNodesGroupedByPlayer = this.groupNodesByPlayer(computerNodes);
        const computerNodesSamePlayer = this.getComputerNodeGroup(computerNodesGroupedByPlayer);

        // Computer: Attack Near Enemies
        // ------------------------------------------------------

        for (const originNode of computerNodesSamePlayer || []) {

            const targetNodes = Phaser.Utils.Array.Shuffle(nodeManager.nearEnemyNodes(originNode, paths));
            const originNodeDefensivePower = originNode.config.defensivePower;
            const originNodeUnits = originNode.counter;

            for (const targetNode of targetNodes) {

                const targetNodeDefensivePower = targetNode.config.defensivePower;
                const targetNodeUnits = targetNode.counter;
                const targetOnWayUnits =  pathManager.searchPathMovement(targetNode, originNode)?.getPayload().counter ?? 0;
                const targetTotalDefense = (targetNodeUnits * targetNodeDefensivePower) + targetOnWayUnits;
                const capturePosibility = (originNodeUnits - targetTotalDefense) * ((originNodeUnits - targetTotalDefense) < 0 ? this.config.attackLoseWeight : this.config.attackWinWeight);
                const movementPercentage = Math.abs(Math.min((targetTotalDefense + this.config.attackGuardUnits)/ originNodeUnits, 1));
                const targetStrategicValue = this.nodeTypePriority(targetNode.config.type);
                const originGuardPriority = (originNodeDefensivePower * this.config.guardPriorityWeight);
                const targetEnemyType = targetNode.player.type === 'neutral' ? this.config.neutralTargetWeight : 0;
                const movementPriority = capturePosibility + targetStrategicValue + targetEnemyType - originGuardPriority;    
                                
                if(movementPriority > this.config.attackMovementPriorityThreshold) {
                    posibleMovements.push({priority: movementPriority, originNode: originNode, targetNode: targetNode, percentage: movementPercentage});
                }   
            }
        }

        const pathMovement = this.createMovementByPriority(posibleMovements, pathManager);
        if(pathMovement) {
            pathMovements.push(pathMovement);
        }

        // Computer: Move Troups from not conflictive node
        // ------------------------------------------------------

        // de todos los nodos de esta computadora obtengo los que necesitan con mas prioridad refuerzos
        const nodesToReforce: string[] = [];
        for (const node of computerNodesSamePlayer || []) {
            const nearEnemyNodes = Phaser.Utils.Array.Shuffle(nodeManager.nearEnemyNodes(node, paths));
            if(nearEnemyNodes.length > 0) { 
                nodesToReforce.push(node.id);
            }
        }

        // de todos los nodos de esta computadora los ordeno en relacion a las unidades
        this.reforceCounter++;
        if(this.reforceCounter === this.config.slowingPrecisionReinforcements) {
            this.reforceCounter = 0;
            computerNodesSamePlayer?.sort((a: Node, b: Node) => {
                return b.counter - a.counter;
            });
        }
        
        let moveBreak = false;
        for (const node of computerNodesSamePlayer || []) {

            // obtengo los nodos enemigos cercanos en relacion al nodo
            const nearEnemyNodes = Phaser.Utils.Array.Shuffle(nodeManager.nearEnemyNodes(node, paths));
            if(nearEnemyNodes.length > 0) {
                continue;
            }

            // obtengo los nodos aliados cercanos en relacion al nodo
            let nearAlliedNodes = Phaser.Utils.Array.Shuffle(nodeManager.nearAlliedNodes(node, paths));
    
            // de los nodos aliados cercanos, me quedo solo los que necesitan refuerzos si los hay
            const nearAlliedNodesToReforce: Node[] = nearAlliedNodes.filter((node: Node) => {
                return nodesToReforce.includes(node.id);
            });
            if (nearAlliedNodesToReforce.length > 0) {
                nearAlliedNodes = Phaser.Utils.Array.Shuffle(nearAlliedNodesToReforce);
            }

            // recorro los nodos aliados en relacion al nodo seleccionado y creo el movimiento
            for (const nearAlliedNode of nearAlliedNodes) {
                const pathMovement = pathManager.createPathMovement(node, nearAlliedNode);
                if(pathMovement) {
                    pathMovements.push(pathMovement);
                }

                moveBreak = true;
                if(moveBreak)  break;  
            }
            
            if(moveBreak)  break; 
        }

        return pathMovements;
    }

    private groupNodesByPlayer(computerNodes: Node[]): { [player: string]: Node[] } {
        const groupedNodes: { [player: string]: Node[] } = {};

        // recorremos todos los nodos de las computadoras y los agrupamos por player
        computerNodes.forEach(node => {
            const player = node.player.id;

            // aprovechamos para formar un arreglo para contar los ataques de cada computer
            if (!this.computerCounters[player]) {
                this.computerCounters[player] = {id: player, attackCounter: 0};
            }

            if (!groupedNodes[player]) {
                groupedNodes[player] = [];
            }
            groupedNodes[player].push(node);
        });

        // si una computer es eliminada, eliminamos su contador de ataque
        Object.values(this.computerCounters).forEach(counter => {
            if (!(counter.id in groupedNodes)) {
                delete this.computerCounters[counter.id];
            }
        });

        return groupedNodes;
    }
        

    // se encarga de seleccionar los nodos de la computadora que realizara el ataque
    private getComputerNodeGroup(groupedNodes:{ [player: string]: Node[] }): Node[] | undefined {

        // con el contador de ataques nos aseguramos de que las computadores varien en cada tic
        const values = Object.values(this.computerCounters);
        values.sort((a, b) => a.attackCounter - b.attackCounter);
        const firstComputerCounter = values[0];
        firstComputerCounter.attackCounter++;

        return groupedNodes[firstComputerCounter.id];
    }

    private nodeTypePriority(nodeType: string): number {
        const typePriorities: { [key: string]: number } = {
            'castle': 140,
            'farm': 100,
            'crop': 100,
            'town': 80,
            'tower': 60,
            'granary': 80,
            'temple': 40,
            'hut': 60,
            'forest': 10,
            'mountain': 10,
            'meadows': -20
        };
    
        return typePriorities[nodeType] || 0;
    }

    private createMovementByPriority(possibleMovements: PossibleMovement[], pathManager: PathManager): PathMovement | null {

        // Encontrar el número negativo más bajo
        const lowestNegativePriority = Math.min(...possibleMovements.map(movement => movement.priority));

        // Ordenar el arreglo por prioridad de MENOR a MAYOR
        possibleMovements.sort((a, b) => a.priority - b.priority);

        // Sumar el número negativo más bajo a todas las prioridades para hacerlas positivas
        const adjustedPriorities = possibleMovements.map(movement => movement.priority + Math.abs(lowestNegativePriority));

        // Se aumenta la posibilidad del mejor resultado en base a una constante
        adjustedPriorities[adjustedPriorities.length-1] += this.config.attackComputerAccuracy;

        // Calcular la suma total de todas las prioridades ajustadas
        const totalPriority = adjustedPriorities.reduce((sum, priority) => sum + priority, 0);

        // Generar un número aleatorio entre 0 y la suma total de prioridades ajustadas
        const random = Math.random() * totalPriority;

        // Iterar sobre los movimientos y seleccionar uno basado en la probabilidad
        let accumulated = 0;
        for (let i = 0; i < possibleMovements.length; i++) {
            accumulated += adjustedPriorities[i];
            if (random <= accumulated) {
                return pathManager.createPathMovement(
                    possibleMovements[i].originNode,
                    possibleMovements[i].targetNode,
                    possibleMovements[i].percentage
                );
            }
        }

        return null;
    }
}